<template>
  <form
    class="d-flex flex-column gap-4 behaviour-csv-upload-form"
    :data-testid="uploadDefinition?.testId"
    @submit.prevent="onSubmit"
  >
    <InfoBox
      v-if="serverResponse"
      :class="{ danger: serverResponse.isError }"
      :title="serverResponse.title"
      :message="serverResponse.message"
      announce-assertive
    />

    <div
      v-if="failedRowMessages.length"
      class="behaviour-csv-upload-row-errors"
      role="alert"
      aria-live="assertive"
      data-testid="behaviour-csv-upload-row-errors"
    >
      <p class="v3-form-label mb-2">
        {{ $t("behaviour_settings.upload_csv_row_errors_title") }}
      </p>
      <ul class="mb-0 ps-3 behaviour-csv-upload-row-errors-list">
        <li
          v-for="(message, index) in failedRowMessages"
          :key="`${message}-${index}`"
        >
          {{ message }}
        </li>
      </ul>
    </div>

    <FileUpload
      :input-id="uploadDefinition?.inputId"
      :accessible-label="fileAccessibleLabel"
      :uploaded-file="form.file"
      :download-example-file-path="uploadDefinition?.sampleFilePath || ''"
      :invalid-feedback="errors.file"
      @update:model-value="onFileUpload"
      @remove-file="onRemoveFile"
    />

    <div class="d-flex justify-content-center align-content-center gap-4">
      <LoadingButton
        type="button"
        rounded
        data-testid="behaviour-csv-upload-cancel"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </LoadingButton>
      <LoadingButton
        type="submit"
        rounded
        solid
        :is-loading="isLoading"
        data-testid="behaviour-csv-upload-submit"
      >
        {{ $t("behaviour_settings.upload_csv_submit") }}
      </LoadingButton>
    </div>
  </form>
</template>

<script>
import behaviourCsvUploadConstants from "@/constants/behaviourCsvUploadConstants.js"
import {
  getBehaviourCsvUploadDefinition,
  getBehaviourCsvUploadFailedRowMessages,
  hasBehaviourCsvUploadFailures,
  hasBehaviourCsvUploadInsertions,
  isValidBehaviourCsvUploadKind,
  parseBehaviourCsvUploadApiError,
  resolveBehaviourCsvUploadFileLabel
} from "@/helpers/behaviourCsvUpload.js"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import FileUpload from "@/v3components/shared/Form/FileUpload.vue"
import useVuelidate from "@vuelidate/core"
import { helpers, required } from "@vuelidate/validators"
import { computed, inject, reactive, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourCsvUploadForm",
  components: {
    FileUpload,
    InfoBox,
    LoadingButton
  },
  props: {
    uploadKind: {
      type: String,
      required: true,
      validator: (value) => isValidBehaviourCsvUploadKind(value)
    }
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")
    const isLoading = ref(false)
    const serverResponse = ref(null)
    const failedRowMessages = ref([])

    const form = reactive({
      file: null
    })

    const uploadDefinition = computed(() =>
      getBehaviourCsvUploadDefinition(props.uploadKind)
    )

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const fileAccessibleLabel = computed(() =>
      resolveBehaviourCsvUploadFileLabel(
        props.uploadKind,
        behaviourTerminology.value,
        t
      )
    )

    const rules = computed(() => ({
      file: {
        required: helpers.withMessage(
          t("behaviour_settings.upload_csv_error_required_file"),
          required
        )
      }
    }))

    const v$ = useVuelidate(rules, form)

    const errors = computed(() => ({
      file: v$?.value?.file?.$error
        ? v$?.value?.file?.$errors
            ?.map((error) => error.$message)
            ?.join(", ") || ""
        : ""
    }))

    const clearServerFeedback = () => {
      serverResponse.value = null
      failedRowMessages.value = []
    }

    const setServerError = (message) => {
      serverResponse.value = {
        isError: true,
        title: t("behaviour_settings.upload_csv_error_title"),
        message: message || t("behaviour_settings.upload_csv_error")
      }
    }

    const buildResultMessage = (result) => {
      const total = Number(result?.totalRows) || 0
      const inserted = Number(result?.inserted) || 0
      const failed = Number(result?.failed) || 0

      if (inserted > 0 && failed === 0) {
        return {
          isError: false,
          title: t("behaviour_settings.upload_csv_success_title"),
          message: t("behaviour_settings.upload_csv_success", {
            inserted,
            total
          })
        }
      }

      if (inserted > 0 && failed > 0) {
        return {
          isError: false,
          title: t("behaviour_settings.upload_csv_partial_success_title"),
          message: t("behaviour_settings.upload_csv_partial_success", {
            inserted,
            failed,
            total
          })
        }
      }

      return {
        isError: true,
        title: t("behaviour_settings.upload_csv_all_failed_title"),
        message: t("behaviour_settings.upload_csv_all_failed", {
          failed,
          total
        })
      }
    }

    const notifyUploadSuccess = (result) => {
      const action = uploadDefinition.value?.uploadSuccessAction
      if (!action || !hasBehaviourCsvUploadInsertions(result)) return

      modal?.setAction?.(action, {
        totalRows: result.totalRows,
        inserted: result.inserted,
        failed: result.failed
      })
    }

    const closeAfterSuccess = () => {
      window.setTimeout(() => {
        emit("cancel")
      }, 1500)
    }

    const onCancel = () => {
      emit("cancel")
    }

    const onFileUpload = (event) => {
      const files = event?.target?.files
      if (files?.length) {
        form.file = files[0]
        event.target.value = ""
        clearServerFeedback()
      }
    }

    const onRemoveFile = () => {
      form.file = null
      clearServerFeedback()
    }

    const onSubmit = async () => {
      const isFormCorrect = await v$.value.$validate()
      if (!isFormCorrect) return

      try {
        isLoading.value = true
        clearServerFeedback()

        const result = await store.dispatch(
          "behaviourSettings/uploadBehaviourCsv",
          {
            uploadKind: props.uploadKind,
            file: form.file
          }
        )

        failedRowMessages.value = getBehaviourCsvUploadFailedRowMessages(
          result?.rowResults
        )
        serverResponse.value = buildResultMessage(result)

        if (hasBehaviourCsvUploadInsertions(result)) {
          notifyUploadSuccess(result)
        }

        if (
          hasBehaviourCsvUploadInsertions(result) &&
          !hasBehaviourCsvUploadFailures(result)
        ) {
          closeAfterSuccess()
        }
      } catch (error) {
        setServerError(parseBehaviourCsvUploadApiError(error))
      } finally {
        isLoading.value = false
      }
    }

    return {
      uploadDefinition,
      form,
      isLoading,
      serverResponse,
      failedRowMessages,
      fileAccessibleLabel,
      errors,
      onCancel,
      onFileUpload,
      onRemoveFile,
      onSubmit,
      UPLOAD_KIND: behaviourCsvUploadConstants.UPLOAD_KIND
    }
  }
}
</script>
